import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import {
  ADD_ITEM,
  ADD_MEMBERSHIP_ITEM,
  REQUEST_IMAGE_UPLOAD_URLS,
  UPDATE_ITEM,
} from './graphql/mutations';
import type {
  BasketAPIAddItemMutationResponseBody,
  BasketAPIAddMembershipItemMutationResponseBody,
  BasketAPIRequestImageUploadUrlsResponseBody,
  BasketAPIUpdateItemMutationResponseBody,
} from './types/BasketAPIResponseBody';
import type { ProductOptionInputWithoutLabel } from './types/graphql';
import { graphqlRequest } from './utils/graphqlRequest';

export interface AddItemMutationVariables {
  code: string;
  currencyCode: CurrencyCode;
  options: ProductOptionInputWithoutLabel;
  quantity: number;
}

export class BasketAPIError extends Error {
  public code: string | undefined;
  public basketApiMessage: string | undefined;
  constructor(message: string, code?: string, basketApiMessage?: string) {
    super(message);
    this.name = 'BasketAPIError';
    this.code = code;
    this.basketApiMessage = basketApiMessage;
  }
}

export const addItem = async (
  productCode: number,
  quantity: number,
  currencyCode: CurrencyCode = 'GBP',
  options: ProductOptionInputWithoutLabel,
) => {
  const response = await graphqlRequest<
    BasketAPIAddItemMutationResponseBody,
    AddItemMutationVariables
  >(ADD_ITEM, {
    code: String(productCode),
    currencyCode,
    quantity,
    options,
  });

  if (!response.data.addItem.success) {
    const firstError = response.data.addItem.errors[0];

    throw new BasketAPIError(
      firstError?.message ?? 'Unknown error',
      firstError?.code,
      firstError?.message,
    );
  }

  return response.data.addItem;
};

interface UpdateItemMutationVariables {
  currencyCode: CurrencyCode;
  id: string;
  options: ProductOptionInputWithoutLabel;
  quantity: number;
}

export const updateItem = async (
  id: string,
  quantity: number,
  currencyCode: CurrencyCode = 'GBP',
  options: ProductOptionInputWithoutLabel,
) => {
  const response = await graphqlRequest<
    BasketAPIUpdateItemMutationResponseBody,
    UpdateItemMutationVariables
  >(UPDATE_ITEM, {
    id,
    quantity,
    currencyCode,
    options,
  });

  if (!response.data.updateItem.success) {
    const firstError = response.data.updateItem.errors[0];

    throw new BasketAPIError(
      firstError?.message ?? 'Unknown error',
      firstError?.code,
      firstError?.message,
    );
  }

  return response.data.updateItem;
};

export interface RequestImageUploadUrlsVariables {
  count?: number;
  optionId?: number | string;
  productCode?: number | string;
}

export const requestImageUploadUrls = async ({
  count,
  optionId,
  productCode,
}: RequestImageUploadUrlsVariables) => {
  const response = await graphqlRequest<
    BasketAPIRequestImageUploadUrlsResponseBody,
    {
      count?: number;
      optionId?: string;
      productCode?: string;
    }
  >(REQUEST_IMAGE_UPLOAD_URLS, {
    count,
    optionId: optionId != null ? String(optionId) : undefined,
    productCode: productCode != null ? String(productCode) : undefined,
  });

  return response.data.requestImageUploadUrls.slots;
};

export const addMembershipItem = async (currency: CurrencyCode) => {
  const response = await graphqlRequest<
    BasketAPIAddMembershipItemMutationResponseBody,
    { currency: CurrencyCode }
  >(ADD_MEMBERSHIP_ITEM, {
    currency,
  });

  if (!response.data.addMembershipItem.success) {
    const errorMessages = response.data.addMembershipItem.errors.map((error) => error.message);

    throw new Error(
      `the addMembershipItem basket-api mutation failed: ${errorMessages.join(', ')}`,
    );
  }

  return response.data.addMembershipItem;
};
